import { uid } from "./studioModel";

// Paths run from the outer group to the immediate parent. Legacy groupId-only
// artwork stays unchanged until a grouping operation is made.
export function groupPath(node) {
  return Array.isArray(node.groupPath) ? node.groupPath : node.groupId ? [{ id: node.groupId, name: "Group" }] : [];
}
export function withGroupPath(node, path) {
  return { ...node, groupPath: path, groupId: path[path.length - 1]?.id || null };
}
export function layerTree(page) {
  const root = { id: null, children: [] }, groups = new Map();
  [...page.elements].reverse().forEach((node) => {
    let parent = root;
    groupPath(node).forEach((group) => {
      if (!groups.has(group.id)) {
        const entry = { ...group, group: true, children: [], parentId: parent.id };
        groups.set(group.id, entry);
        parent.children.push(entry);
      }
      parent = groups.get(group.id);
    });
    parent.children.push({ ...node, parentId: parent.id });
  });
  return root.children;
}
export function layerMembers(entry) {
  return entry.group ? entry.children.flatMap(layerMembers) : [entry];
}
export function groupMembers(page, id) {
  return page.elements.filter((node) => groupPath(node).some((g) => g.id === id));
}
export function selectedGroups(page, selected, focusId) {
  const focused = focusedEntry(page, selected, focusId);
  if (focused) {
    const members = layerMembers(focused).filter((n) => !n.hidden && !n.locked);
    return focused.group && members.length ? [{ id: focused.id, members }] : [];
  }
  const ids = new Set(selected), result = [];
  const visit = (entry) => {
    if (!entry.group) return;
    const members = layerMembers(entry).filter((n) => !n.hidden && !n.locked);
    if (members.length && members.every((n) => ids.has(n.id))) result.push({ id: entry.id, members });
    else entry.children.forEach(visit);
  };
  layerTree(page).forEach(visit);
  return result;
}
function focusedEntry(page, selected, focusId) {
  if (!focusId) return null;
  let found = null;
  const visit = (entries) => entries.forEach((entry) => {
    if (entry.id === focusId && layerMembers(entry).length === selected.length && layerMembers(entry).every((n) => selected.includes(n.id))) found = entry;
    else if (entry.group) visit(entry.children);
  });
  visit(layerTree(page));
  return found;
}
export function selectionEntries(page, selected, focusId) {
  const focused = focusedEntry(page, selected, focusId);
  if (focused) return [focused];
  const ids = new Set(selected), entries = [];
  const visit = (entry) => {
    if (layerMembers(entry).every((n) => ids.has(n.id))) entries.push(entry);
    else if (entry.group) entry.children.forEach(visit);
  };
  layerTree(page).forEach(visit);
  return entries;
}
export function createGroup(page, selected) {
  const entries = selectionEntries(page, selected);
  if (entries.length < 2 || entries.some((e) => layerMembers(e).some((n) => n.locked))) return page;
  const paths = entries.map((e) => e.group ? groupPath(layerMembers(e)[0]).slice(0, groupPath(layerMembers(e)[0]).findIndex((g) => g.id === e.id)) : groupPath(e));
  let common = paths[0];
  paths.forEach((path) => { common = common.filter((g, i) => path[i]?.id === g.id); });
  const group = { id: uid(), name: "Group" }, ids = new Set(selected);
  const suffixes = new Map();
  entries.forEach((entry, i) => layerMembers(entry).forEach((n) => suffixes.set(n.id, groupPath(n).slice(paths[i].length))));
  const elements = page.elements.map((n) => ids.has(n.id) ? withGroupPath(n, [...common, group, ...suffixes.get(n.id)]) : n);
  // A group is one stacking unit. Keep its children together at the frontmost
  // selected layer, retaining their relative order and all artwork geometry.
  const moving = elements.filter((n) => ids.has(n.id));
  const index = elements.findLastIndex ? elements.findLastIndex((n) => ids.has(n.id)) : Math.max(...elements.map((n, i) => ids.has(n.id) ? i : -1));
  const before = elements.slice(0, index + 1).filter((n) => !ids.has(n.id));
  return { ...page, elements: [...before, ...moving, ...elements.slice(index + 1)] };
}
export function ungroupSelection(page, selected, focusId) {
  const entries = selectionEntries(page, selected, focusId), remove = new Set(entries.filter((e) => e.group).map((e) => e.id));
  // A precise child selection can also be lifted out of its immediate group.
  return { ...page, elements: page.elements.map((n) => {
    const path = groupPath(n);
    if (path.some((g) => remove.has(g.id))) return withGroupPath(n, path.filter((g) => !remove.has(g.id)));
    return selected.includes(n.id) && !n.locked ? withGroupPath(n, path.slice(0, -1)) : n;
  }) };
}
export function renameGroup(page, id, name) {
  if (!name.trim()) return page;
  return { ...page, elements: page.elements.map((n) => withGroupPath(n, groupPath(n).map((g) => g.id === id ? { ...g, name: name.trim().slice(0, 100) } : g))) };
}
export function moveLayers(page, sourceIds, targetId, placement = "inside") {
  const tree = layerTree(page), lookup = new Map();
  const visit = (entries) => entries.forEach((entry) => { lookup.set(entry.id, entry); if (entry.group) visit(entry.children); });
  visit(tree);
  const sources = sourceIds.map((id) => lookup.get(id)).filter(Boolean);
  const movingIds = new Set(sources.flatMap(layerMembers).map((n) => n.id));
  const moving = page.elements.filter((n) => movingIds.has(n.id));
  const target = lookup.get(targetId);
  if (!moving.length || moving.some((n) => n.locked) || (targetId && !target)) return page;
  if (target && sources.some((source) => source.id === target.id || (source.group && (target.group ? groupPath(layerMembers(target)[0]).some((g) => g.id === source.id) : movingIds.has(target.id))))) return page;
  let destination = [];
  if (target) {
    const path = groupPath(layerMembers(target)[0]);
    destination = target.group ? path.slice(0, path.findIndex((g) => g.id === target.id) + (placement === "inside" ? 1 : 0)) : path;
  }
  const changed = new Map();
  sources.forEach((source) => {
    const firstPath = groupPath(layerMembers(source)[0]);
    const cut = source.group ? firstPath.findIndex((g) => g.id === source.id) : firstPath.length;
    layerMembers(source).forEach((node) => changed.set(node.id, withGroupPath(node, [...destination, ...groupPath(node).slice(cut)])));
  });
  const rest = page.elements.filter((n) => !movingIds.has(n.id));
  const targetIds = new Set(target ? layerMembers(target).map((n) => n.id) : []);
  const positions = rest.map((n, i) => targetIds.has(n.id) ? i : -1).filter((i) => i >= 0);
  if (target && !positions.length) return page;
  const index = target ? placement === "after" ? Math.min(...positions) : Math.max(...positions) + 1 : rest.length;
  rest.splice(index, 0, ...moving.map((n) => changed.get(n.id)));
  return { ...page, elements: rest };
}
export function canvasGroupSelection(page, node, selected, direct = false, focusId) {
  if (direct || node.locked) return { ids: [node.id], focusId: node.id, drill: null };
  const paths = groupPath(node);
  const candidates = paths.map((g) => ({ focusId: g.id, ids: groupMembers(page, g.id).filter((n) => !n.hidden && !n.locked).map((n) => n.id) }));
  candidates.push({ focusId: node.id, ids: [node.id] });
  const matches = (candidate) => candidate.ids.length === selected.length && candidate.ids.every((id) => selected.includes(id));
  let exact = candidates.findIndex((candidate) => candidate.focusId === focusId && matches(candidate));
  if (exact < 0) exact = candidates.findIndex(matches);
  if (exact >= 0) return { ...candidates[exact], drill: candidates[exact + 1]?.ids || null, drillFocusId: candidates[exact + 1]?.focusId };
  if (selected.includes(node.id)) return { ids: selected, focusId, drill: null };
  const inside = candidates.findIndex((candidate) => candidate.ids.some((id) => selected.includes(id)));
  return { ...candidates[inside >= 0 ? Math.min(inside + 1, candidates.length - 1) : 0], drill: null };
}
// Shift toggles the clicked selection unit, never the entire active selection.
export function canvasShiftSelection(page, node, selected, direct = false, focusId) {
  const existing = !direct && selected.includes(node.id)
    ? selectionEntries(page, selected, focusId).find((entry) => layerMembers(entry).some((member) => member.id === node.id))
    : null;
  const clicked = existing
    ? layerMembers(existing).map((member) => member.id)
    : canvasGroupSelection(page, node, [], direct).ids;
  const toggled = new Set(clicked);
  return clicked.every((id) => selected.includes(id))
    ? selected.filter((id) => !toggled.has(id))
    : Array.from(new Set([...selected, ...clicked]));
}
export function reorderLayers(page, selected, delta, focusId) {
  let next = page;
  const entries = selectionEntries(page, selected, focusId);
  // Operate on siblings, so raising a group never separates its children.
  (delta > 0 ? entries : [...entries].reverse()).forEach((entry) => {
    let siblings = layerTree(next);
    const find = (list) => {
      if (list.some((e) => e.id === entry.id)) { siblings = list; return true; }
      return list.some((e) => e.group && find(e.children));
    };
    find(siblings);
    const index = siblings.findIndex((e) => e.id === entry.id), target = siblings[index - delta];
    if (target && !entries.some((e) => e.id === target.id)) next = moveLayers(next, [entry.id], target.id, delta > 0 ? "before" : "after");
  });
  return next;
}
// Copy/transfer only complete groups. A single child leaves its ancestors behind.
export function portableSelection(elements, selected, focusId) {
  const ids = new Set(selected), complete = new Set();
  const groups = new Map();
  elements.forEach((n) => groupPath(n).forEach((g) => {
    if (!groups.has(g.id)) groups.set(g.id, []);
    groups.get(g.id).push(n.id);
  }));
  groups.forEach((members, id) => { if (members.every((member) => ids.has(member))) complete.add(id); });
  const focused = focusedEntry({ elements }, selected, focusId);
  if (focused) {
    const path = groupPath(layerMembers(focused)[0]);
    const parents = focused.group ? path.slice(0, path.findIndex((g) => g.id === focused.id)) : path;
    parents.forEach((g) => complete.delete(g.id));
  }
  return elements.filter((n) => ids.has(n.id)).map((n) => {
    const path = groupPath(n);
    return path.some((g) => !complete.has(g.id)) ? withGroupPath(n, path.filter((g) => complete.has(g.id))) : n;
  });
}
